// src/components/products/sensaphone/SensaphoneSpecialCases.jsx
import React from "react";
import { DocumentIcon } from "@heroicons/react/24/outline";
import RichTextRenderer from "../../common/RichTextRenderer";
import ProductContainerImage from "../ProductContainerImage";
import { cellTextForRow } from "./sensaphoneTableCellText";

/** Multiple media from Strapi: array, single object, or null. */
const normalizeMediaList = (media) => {
  if (!media) return [];
  const list = Array.isArray(media) ? media : [media];
  return list.filter((item) => item && (item.url || item?.formats?.large?.url));
};

const resolveMediaUrl = (entry) =>
  entry?.url || entry?.formats?.large?.url || entry?.formats?.medium?.url || null;

const isPdfMedia = (entry) => {
  if (!entry) return false;
  const mime = (entry.mime || "").toLowerCase();
  const url = (resolveMediaUrl(entry) || "").toLowerCase();
  return mime.includes("pdf") || url.endsWith(".pdf");
};

const isImageMedia = (entry) => {
  if (!entry) return false;
  const mime = (entry.mime || "").toLowerCase();
  const url = (resolveMediaUrl(entry) || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  return /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(url);
};

/** Hardcoded: "Schematic diagram" block uses one image + one PDF; show image only, click opens PDF. */
const SCHEMATIC_FEATURE_TITLE_REGEX = /schematic/i;

const SpecialCaseSchematicImageOpensPdf = ({ imageEntry, pdfEntry }) => {
  const imgUrl = resolveMediaUrl(imageEntry);
  const pdfUrl = resolveMediaUrl(pdfEntry);
  if (!imgUrl || !pdfUrl) return null;

  const alt =
    imageEntry.alternativeText ||
    imageEntry.name ||
    "Schematic diagram";

  return (
    <button
      type="button"
      onClick={() => window.open(pdfUrl, "_blank", "noopener,noreferrer")}
      className="group block w-full max-w-4xl text-left p-0 m-0 border-0 bg-transparent cursor-pointer rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
      title="Open schematic PDF"
    >
      <ProductContainerImage sizing="intrinsic" src={imgUrl} alt={alt} />
      <p className="mt-2 text-sm text-gray-500 group-hover:text-green-700 transition-colors">
        Click image to open PDF
      </p>
    </button>
  );
};

const SpecialCaseMediaItem = ({ entry, index }) => {
  const url = resolveMediaUrl(entry);
  if (!url) return null;

  const alt = entry.alternativeText || entry.name || `Media ${index + 1}`;
  const label =
    entry.name || entry.alternativeText || `PDF ${index + 1}`;

  if (isImageMedia(entry)) {
    return <ProductContainerImage sizing="intrinsic" src={url} alt={alt} />;
  }

  if (isPdfMedia(entry)) {
    return (
      <button
        type="button"
        onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
        className="group flex flex-col items-center justify-center gap-3 w-full min-h-[200px] rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 hover:border-green-500 hover:bg-green-50/50 transition-colors p-6"
      >
        <div className="w-16 h-16 rounded-xl bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
          <DocumentIcon className="w-10 h-10 text-red-600" />
        </div>
        <span className="text-sm sm:text-base font-semibold text-gray-800 group-hover:text-green-700 text-center px-2 line-clamp-2">
          {label}
        </span>
        <span className="text-xs text-gray-500">Open PDF</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
      className="text-sm font-medium text-green-700 hover:text-green-900 underline text-left p-2"
    >
      Open file{label ? `: ${label}` : ""}
    </button>
  );
};

const SpecialCaseFeatureMedia = ({ media, featureTitle, safeText }) => {
  const items = normalizeMediaList(media);
  if (items.length === 0) return null;

  const titleLabel = safeText(featureTitle);
  const isSchematicBlock =
    titleLabel && SCHEMATIC_FEATURE_TITLE_REGEX.test(titleLabel);

  const firstImage = items.find((e) => isImageMedia(e));
  const firstPdf = items.find((e) => isPdfMedia(e));

  if (isSchematicBlock && firstImage && firstPdf) {
    return (
      <div className="mt-4">
        <SpecialCaseSchematicImageOpensPdf
          imageEntry={firstImage}
          pdfEntry={firstPdf}
        />
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col sm:flex-row flex-wrap items-start gap-4 lg:gap-6">
      {items.map((entry, index) => (
        <SpecialCaseMediaItem
          key={entry.id || entry.documentId || index}
          entry={entry}
          index={index}
        />
      ))}
    </div>
  );
};

const SpecialCaseTable = ({ header1, header2, rows, safeText }) => {
  if (!rows || rows.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b-2 border-teal-400/40 bg-teal-900/12">
            <th
              scope="col"
              className="px-4 sm:px-6 py-3 text-left text-sm sm:text-base font-semibold text-teal-950 w-1/4"
            >
              <span className="sr-only">Type</span>
            </th>
            <th
              scope="col"
              className="px-4 sm:px-6 py-3 text-left text-sm sm:text-base font-semibold text-teal-950 w-[37.5%]"
            >
              {safeText(header1)}
            </th>
            <th
              scope="col"
              className="px-4 sm:px-6 py-3 text-left text-sm sm:text-base font-semibold text-teal-950 w-[37.5%]"
            >
              {safeText(header2)}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.id || index}
              className={`border-b product-table-row ${
                index % 2 === 0 ? "product-stripe-even" : "bg-white/85"
              }`}
            >
              <td className="px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-semibold text-teal-950 align-top whitespace-pre-line">
                {cellTextForRow(row.type, safeText)}
              </td>
              <td className="px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base text-teal-900/85 align-top whitespace-pre-line">
                {cellTextForRow(row.text1, safeText)}
              </td>
              <td className="px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base text-teal-900/85 align-top whitespace-pre-line">
                {cellTextForRow(row.text2, safeText)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SpecialCaseDownloadList = ({ title, files, safeText }) => {
  if (!title || !files || files.length === 0) return null;

  return (
    <div className="space-y-4">
      <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
        {safeText(title)}
      </h3>
      <div className="space-y-4 sm:space-y-6">
        {files.map((file, index) => {
          const fileUrl =
            file.download_file?.url ||
            file.download_file?.[0]?.url ||
            file.url;
          const rawTitle = file.download_title;
          const fileTitle = safeText(rawTitle) || `Download ${index + 1}`;
          const isPDF = fileUrl && fileUrl.toLowerCase().endsWith(".pdf");

          return (
            <button
              key={file.id || index}
              type="button"
              onClick={() => {
                if (fileUrl) {
                  window.open(fileUrl, "_blank", "noopener,noreferrer");
                }
              }}
              className="group w-full bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-green-100 p-4 md:p-6 transition-all duration-300 hover:shadow-xl hover:scale-[1.01] flex items-center gap-4 text-left"
            >
              <div className="flex-shrink-0">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
                  <DocumentIcon className="h-6 w-6 sm:h-7 sm:w-7 text-red-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 group-hover:text-green-600 transition-colors duration-200 line-clamp-2 block">
                  {fileTitle}
                </span>
                {isPDF && (
                  <p className="mt-1 text-xs sm:text-sm text-gray-500">
                    PDF Document
                  </p>
                )}
              </div>
              <div className="flex-shrink-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full product-btn-icon transition-colors duration-200 flex items-center justify-center">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 3h7m0 0v7m0-7L10 14"
                    />
                  </svg>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Wine Storage / Web600 style blocks from cas_special_case relation.
 */
const SensaphoneSpecialCases = ({ cases, safeText }) => {
  if (!cases || cases.length === 0) return null;

  return (
    <>
      {cases.map((specialCase, caseIndex) => {
        const name = safeText(specialCase.name);
        const features = specialCase.features || [];
        const specTitle = safeText(specialCase.specification_title);
        const tableRows = specialCase.table || [];
        const dlTitle = safeText(specialCase.download_title);
        const downloads = specialCase.download || [];

        const hasFeatures = features.length > 0;
        const hasTable = tableRows.length > 0;
        const hasDownloads = dlTitle && downloads.length > 0;

        if (!name && !hasFeatures && !hasTable && !hasDownloads) {
          return null;
        }

        return (
          <div
            key={specialCase.id || caseIndex}
            className="mb-12 md:mb-16"
          >
            <div className="product-panel-elevated rounded-3xl shadow-2xl overflow-hidden">
              {name ? (
                <div className="product-panel-header">
          <h2>
                    {name}
                  </h2>
                </div>
              ) : null}

              <div className="p-6 sm:p-8 lg:p-10 space-y-10 md:space-y-12">
                {hasFeatures &&
                  features.map((feature, fi) => {
                    const fTitle = safeText(feature.title);
                    const content = feature.content;
                    const hasContent =
                      Array.isArray(content) && content.length > 0;

                    return (
                      <div
                        key={feature.id || fi}
                        className="border-b border-gray-100 last:border-0 pb-10 last:pb-0"
                      >
                        {fTitle ? (
                          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                            {fTitle}
                          </h3>
                        ) : null}
                        {hasContent ? (
                          <div className="prose prose-lg max-w-none text-gray-800">
                            <RichTextRenderer
                              content={content}
                              textColorClass="text-gray-800"
                              linkColorClass="text-blue-600 hover:text-blue-800 underline text-lg lg:text-xl"
                            />
                          </div>
                        ) : null}
                        <SpecialCaseFeatureMedia
                          media={feature.media}
                          featureTitle={feature.title}
                          safeText={safeText}
                        />
                      </div>
                    );
                  })}

                {hasTable ? (
                  <div className="space-y-4">
                    {specTitle ? (
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                        {specTitle}
                      </h3>
                    ) : null}
                    <SpecialCaseTable
                      header1={specialCase.header1}
                      header2={specialCase.header2}
                      rows={tableRows}
                      safeText={safeText}
                    />
                  </div>
                ) : null}

                {hasDownloads ? (
                  <SpecialCaseDownloadList
                    title={specialCase.download_title}
                    files={downloads}
                    safeText={safeText}
                  />
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default SensaphoneSpecialCases;
